/**
 * 🔴 **A COMPANY, DRAWN IN ITS OWN COLOURS** (PNK-109, D77 × D102).
 *
 * Devin: *"when an org is selected or viewed it's got to get their look and feel… if we're looking at
 * an actor represented by one of them we should see their card."*
 *
 * The research engine reads a company's palette off their own website and a person accepts it; from
 * then on the `organisations` row carries `brandColours`, `brandFont`, `brandVoice` and a hero
 * pointer. This file is the ONE place that turns those into something a screen can draw, so twelve
 * modules cannot each invent their own idea of "their colour".
 *
 * 🔴 **NO COLOUR IS WRITTEN IN THIS FILE, and the palette test enforces it.** Every value here either
 * came from the company's own row at runtime, or is one of the shell's tenant tokens. That is not a
 * lint technicality — it is what makes the fallback CORRECT: a company nobody has researched is drawn
 * in the TENANT's colour, which is the right answer, rather than in a pink somebody typed in 2026.
 *
 * 🔴 **A COMPANY'S PALETTE COLOURS A CARD, NEVER THE PRODUCT** (D28). The tenant row skins the Desk.
 * These variables are set on ONE element and inherited by its subtree, so a supplier's green reaches
 * the supplier's card and stops there.
 */

import { tintIndex } from "./identity";

/** What the API carries on every organisation read. These four fields are the "look". */
export interface CompanyLookSource {
  brandColours?: readonly string[] | null;
  brandFont?: string | null;
  brandVoice?: string | null;
  heroSha256?: string | null;
  /**
   * 🔴 **WHAT THEIR MARK READS ON** (D139) — `own` · `light` · `dark` · `either`, measured by the
   * backend from the logo's own bytes, or null before it has looked. Not part of the palette: it
   * decides the TILE a `PinkMark` puts their logo on, and it travels on the same row so every screen
   * that already hands a row to `look` gets it without being told.
   */
  logoBackground?: string | null;
}

/** The custom properties a subtree is handed. Values are colours from the row, or token references. */
export type LookStyle = Record<string, string>;

/**
 * 🔴 **WHERE THE ACCENT CAME FROM** (D164 rule 5) — in the order it is looked for.
 *
 *   · `palette` — their own `brandColours`, which a person accepted (D102). Always wins.
 *   · `logo` — read off their mark's pixels by `accentFromPixels`, never written back to the row.
 *   · `name` — neither: the `PinkFace` tint for their name, so a company is never drawn grey.
 *   · `tenant` — the caller passed no fallback at all, and the tenant's primary is the answer it always was.
 */
export type LookBasis = "palette" | "logo" | "name" | "tenant";

/** What a caller who can see more than the row hands `companyLook`, below `brandColours`. */
export interface LookFallback {
  /** The accent `accentFromPixels` read off their fetched mark, or null before (or without) one. */
  derived?: string | null;
  /** Their name — with no palette and no mark, the tint `PinkFace` gives the same name. */
  name?: string | null;
}

export interface CompanyLook {
  /** True when the accent is the company's own — their palette, or their logo; false means tenant tokens. */
  theirs: boolean;
  basis: LookBasis;
  /** The colour a card accents with — their most usable one, or the tenant's primary. */
  accent: string;
  /** `light` or `dark`: which way text ON the accent has to go. Never a colour, so it survives a theme. */
  onAccent: "light" | "dark";
  /** Their palette, normalised and in order. Empty when they have none. */
  colours: readonly string[];
  font: string | null;
  voice: string | null;
  hasHero: boolean;
  /** Bind to `:style` on the element that opens the subtree. */
  style: LookStyle;
}

/** The tenant's own colour, as the shell publishes it. The fallback for every company with no palette. */
export const TENANT_ACCENT = "rgb(var(--v-theme-primary))";

/**
 * 🔴 **THE SIX NAME TINTS, AS ACCENTS** — the same six tenant roles, in the same order, as `ui.css`'s
 * `.pk-face--t0` … `--t5`, so a company with nothing of its own has the accent its `PinkFace` already has.
 * Tokens, not colours: a tenant that re-skins the Desk re-skins these with it.
 */
export const NAME_TINTS: readonly string[] = ["primary", "secondary", "info", "success", "warning", "error"].map((role) => `rgb(var(--v-theme-${role}))`);

/**
 * 🔴 **A COMPANY'S NAME TINT IS NEVER THE TENANT'S COLOUR** (D164 rule 5). Tint 0 is the tenant's primary — the
 * very `TENANT_ACCENT` a company falls back to when nothing is known — so a name that hashes to it would be drawn
 * in the Desk's colour while claiming to be its own. It moves to the next tint instead. Every other name keeps the
 * tint its `PinkFace` already has.
 */
export function companyTint(name: string | null): string {
  const index = tintIndex(name, "organisation");
  const tint = NAME_TINTS[index] ?? TENANT_ACCENT;
  return tint === TENANT_ACCENT ? (NAME_TINTS[(index + 1) % NAME_TINTS.length] ?? TENANT_ACCENT) : tint;
}

/** `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa` → the six-digit form. Anything else is not ours to guess. */
export function sixDigit(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(value)) return null;
  const digits = value.slice(1);
  if (digits.length <= 4) {
    return `#${[...digits.slice(0, 3)].map((digit) => digit + digit).join("")}`;
  }
  return `#${digits.slice(0, 6)}`;
}

const channels = (hex: string): [number, number, number] | null => {
  const six = sixDigit(hex);
  if (six === null) return null;
  return [1, 3, 5].map((at) => Number.parseInt(six.slice(at, at + 2), 16)) as [number, number, number];
};

/**
 * Relative luminance, sRGB, the WCAG definition. Used for ONE thing: deciding whether text on this
 * colour goes light or dark. It never ranks a company and never orders a list (D15).
 */
export function luminance(hex: string): number {
  const rgb = channels(hex);
  if (rgb === null) return 0;
  const linear = rgb.map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (linear[0] ?? 0) + 0.7152 * (linear[1] ?? 0) + 0.0722 * (linear[2] ?? 0);
}

/** How far from grey a colour is, 0–1. A brand has one; page-builder chrome does not. */
export function chroma(hex: string): number {
  const rgb = channels(hex);
  if (rgb === null) return 0;
  return (Math.max(...rgb) - Math.min(...rgb)) / 255;
}

/** Text on this colour: `dark` ink on a light ground, `light` ink on a dark one. */
export const inkOn = (hex: string): "light" | "dark" => (luminance(hex) > 0.42 ? "dark" : "light");

/**
 * 🔴 **THE ACCENT IS CHOSEN, NOT TAKEN.**
 *
 * A site's most FREQUENT colour is almost always its background — white, or a near-black. Accenting
 * a card with a company's white would draw nothing and read as a bug. So the accent is the first
 * colour in their own order that a reader could actually see against either theme: away from both
 * ends of the luminance range, and far enough from grey to look deliberate.
 *
 * If nothing qualifies — a genuinely monochrome brand — a mid-luminance neutral is taken rather than
 * nothing, because a grey chosen from THEIR palette still belongs to them. Only an empty palette
 * falls through to the tenant.
 */
export function accentFrom(colours: readonly string[]): string | null {
  const normalised = colours.map(sixDigit).filter((hex): hex is string => hex !== null);
  const usable = normalised.filter((hex) => {
    const light = luminance(hex);
    return light > 0.03 && light < 0.82;
  });
  return usable.find((hex) => chroma(hex) > 0.12) ?? usable[0] ?? null;
}

/** Below this alpha a pixel is the ground the logo sits on, not the logo. */
const OPAQUE = 128;
/** A bucket must hold this share of the opaque pixels to be a colour of the mark rather than anti-aliasing. */
const WELL_POPULATED = 0.02;
/** Channel bits kept per bucket: eight levels a channel, 512 buckets — coarse enough to pool one ink. */
const BUCKET_SHIFT = 5;
/** Below this chroma a colour reads as grey (the same line `accentFrom` draws). */
const CHROMATIC = 0.12;
/** The ends of the lightness range: near-white is the plate, near-black is ink with no hue to lend. */
const NEAR_WHITE = 0.92;
const NEAR_BLACK = 0.08;
/** The warm neutral: a hue in the amber quarter, a saturation too low to read as a colour. */
const WARM_HUE = 32;
const WARM_SATURATION = 0.08;
/** Its lightness is the ink's, pulled into the band an accent can be seen in on either theme. */
const NEUTRAL_DARKEST = 0.3;
const NEUTRAL_LIGHTEST = 0.62;

const hexOf = (rgb: readonly number[]): string =>
  `#${rgb.map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0")).join("")}`;

/** HSL → RGB, the CSS definition — so the neutral is built from a tone, not typed in. */
function fromHsl(hue: number, saturation: number, lightness: number): number[] {
  const k = (n: number): number => (n + hue / 30) % 12;
  const a = saturation * Math.min(lightness, 1 - lightness);
  return [0, 8, 4].map((n) => 255 * (lightness - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))));
}

/**
 * 🔴 **A COMPANY WITH A LOGO AND NO PALETTE IS NEVER DRAWN GREY** (D164 rule 5).
 *
 * Devin: *"Missing colours: derive from the logo or show a tasteful neutral built from the logo — never plain
 * grey."* APM is the case: a mark on a light plate and `brandColours` null, so every screen drew it in the
 * tenant's colour — the Desk's, not theirs.
 *
 * Handed the RGBA bytes of their mark as `getImageData` returns them, this reads what the mark is made of:
 *
 *   · transparent pixels are the ground, and near-white and near-black ones are the plate or hueless ink;
 *   · what is left is pooled into coarse buckets, and the MOST CHROMATIC bucket that holds a real share of the
 *     mark is the accent — a speck of anti-aliasing magenta never beats a navy wordmark;
 *   · a mark with no such bucket — white, black, grey — gets a WARM NEUTRAL at its own ink's tone, pulled into
 *     the band an accent is visible in, so a pale mark gives a pale neutral and a black one a deep one;
 *   · an image with no opaque pixel at all says nothing, and the caller falls through to the name tint.
 *
 * Pure and deterministic: the same bytes give the same answer in any order, with ties broken by the bucket's
 * own key. Nothing is written back — a person TAKES a palette (D102); this only draws one until they do.
 */
export function accentFromPixels(rgba: ArrayLike<number>): string | null {
  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();
  let opaque = 0;
  let inkLightness = 0;
  for (let at = 0; at + 3 < rgba.length; at += 4) {
    if ((rgba[at + 3] ?? 0) < OPAQUE) continue;
    const r = rgba[at] ?? 0;
    const g = rgba[at + 1] ?? 0;
    const b = rgba[at + 2] ?? 0;
    const high = Math.max(r, g, b);
    const low = Math.min(r, g, b);
    const lightness = (high + low) / 2 / 255;
    opaque += 1;
    inkLightness += lightness;
    if (lightness > NEAR_WHITE || lightness < NEAR_BLACK || (high - low) / 255 <= CHROMATIC) continue;
    const key = ((r >> BUCKET_SHIFT) << 6) | ((g >> BUCKET_SHIFT) << 3) | (b >> BUCKET_SHIFT);
    const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 };
    buckets.set(key, { count: bucket.count + 1, r: bucket.r + r, g: bucket.g + g, b: bucket.b + b });
  }
  if (opaque === 0) return null;

  const floor = Math.max(1, opaque * WELL_POPULATED);
  const candidates = [...buckets.entries()]
    .filter(([, bucket]) => bucket.count >= floor)
    .map(([key, bucket]) => ({ key, count: bucket.count, hex: hexOf([bucket.r / bucket.count, bucket.g / bucket.count, bucket.b / bucket.count]) }))
    .filter((candidate) => accentFrom([candidate.hex]) !== null && chroma(candidate.hex) > CHROMATIC)
    .sort((one, two) => chroma(two.hex) - chroma(one.hex) || two.count - one.count || one.key - two.key);
  const chosen = candidates[0];
  if (chosen !== undefined) return chosen.hex;

  const tone = Math.max(NEUTRAL_DARKEST, Math.min(NEUTRAL_LIGHTEST, inkLightness / opaque));
  return hexOf(fromHsl(WARM_HUE, WARM_SATURATION, tone));
}

/** Their palette, normalised, de-duplicated and capped — the same six the column holds. */
export function paletteOf(colours: readonly string[] | null | undefined): string[] {
  const kept: string[] = [];
  for (const raw of colours ?? []) {
    const hex = sixDigit(raw);
    if (hex === null || kept.includes(hex)) continue;
    kept.push(hex);
    if (kept.length === 6) break;
  }
  return kept;
}

/**
 * 🔴 **A TYPEFACE IS A NAME, NOT A PROMISE.** The research reads what their site LOADS; Pinksy does
 * not fetch a stranger's webfont on their behalf — that is a request to a third party on every page
 * view, made on behalf of a person who never asked for it. So the family is offered to the browser
 * and the kit's own stack catches it: a company whose face happens to be installed gets it, and
 * everybody else gets the Desk's, which is the right degradation.
 *
 * 🔴 A family name reaches a CSS declaration, and this string came off somebody else's website. So it
 * is cleaned by ALLOWING what a typeface name is made of — letters, digits, spaces, hyphens — rather
 * than by forbidding what today's attack happens to look like. A blacklist is a list somebody has to
 * keep adding to; this one cannot be got past by a character nobody thought of.
 */
export function fontStack(family: string | null | undefined): string | null {
  const name = (family ?? "")
    .replace(/[^\p{L}\p{N} -]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (name === "" || name.length > 64) return null;
  return `"${name}", var(--pk-look-font-fallback, inherit)`;
}

/**
 * The whole look, as one object. `style` is what a component binds; everything else is what a
 * component reads to decide what to draw at all.
 */
export function companyLook(source: CompanyLookSource | null | undefined, fallback?: LookFallback | null): CompanyLook {
  const colours = paletteOf(source?.brandColours);
  const chosen = accentFrom(colours);
  // 🔴 Below the palette, never above it: a colour a person accepted beats one a canvas guessed at.
  const derived = chosen === null ? sixDigit(fallback?.derived ?? "") : null;
  const own = chosen ?? derived;
  const named = typeof fallback?.name === "string";
  const basis: LookBasis = chosen !== null ? "palette" : derived !== null ? "logo" : named ? "name" : "tenant";
  const accent = own ?? (named ? companyTint(fallback?.name ?? null) : TENANT_ACCENT);
  // 🔴 With no colour of their own the accent is a token whose value this code cannot see, so the ink question
  // cannot be answered here — the tenant's own `on-primary` answers it, and `light` is what the
  // stylesheet reads as "use the token".
  const onAccent = own === null ? "light" : inkOn(own);
  const font = fontStack(source?.brandFont);
  const style: LookStyle = { "--pk-look-accent": accent };
  if (own !== null) style["--pk-look-on-accent"] = onAccent === "dark" ? "var(--pk-look-ink-dark)" : "var(--pk-look-ink-light)";
  if (font !== null) style["--pk-look-font"] = font;
  for (const [at, colour] of colours.entries()) style[`--pk-look-c${at + 1}`] = colour;

  return {
    theirs: own !== null,
    basis,
    accent,
    onAccent,
    colours,
    font,
    voice: (source?.brandVoice ?? "").trim() === "" ? null : (source?.brandVoice ?? "").trim(),
    hasHero: typeof source?.heroSha256 === "string" && source.heroSha256 !== "",
    style,
  };
}
